import React, { useState, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import {
  EntityType,
  EntityTypeEnum,
  EntityState,
  ReferenceDataManagementEvent,
  ReferenceDataEntityType,
  CollateralBasket,
  CollateralBasketType,
  ProductSector,
  WorkflowActions,
  EntityIdAndType,
  Product,
  ValidationIssue
} from 'api'
import { ModalWindow, ModalFooter, Groupbox } from 'view/components/panels'
import { DialogRow, DialogField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Tabs } from 'view/components/tabs'
import { CollateralBasketOverviewPanel } from './CollateralBasketOverviewPanel'
import { CollateralBasketProductsPanel } from './CollateralBasketProductsPanel'
import { CollateralBasketPermissionsPanel } from './CollateralBasketPermissionsPanel'
import { createWorkflowReferenceDataEntitySelector, createWorkflowReferenceDataAvailableEventsSelector } from 'selectors/workflowSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { collateralBasketProductSectorIdsForTypeGetterSelector } from 'selectors/collateralBasketSelectors'
import { Locale, Translation } from 'locale'

import { createInvalidValueValidationIssue, Severity } from 'model'
import { notNilFilter } from 'utils'

export interface CollateralBasketPanelProps {
  collateralBasket: CollateralBasket
  collateralBasketProductSector: ProductSector | null
  readOnly: boolean
  onUpdate: (partialCollateralBasket: Partial<CollateralBasket>) => void
}
export type CollateralBasketDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  workflowId: string
  entityId: string
  onClose?: () => unknown
}

interface StateProps {
  collateralBasketEntity: ReferenceDataEntityType<CollateralBasket> | null
  availableEvents: ReferenceDataManagementEvent[]
  getProductSector: (productSectorId: string) => ProductSector | null
  getProductSectorIdsForType: (basketType: CollateralBasketType) => string[]
  getProduct: (productId: string) => Product | null
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: CollateralBasket; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: CollateralBasketDialogProps) => ownProps.workflowId
const entityIdSelector = (_state: {}, ownProps: CollateralBasketDialogProps) => ownProps.entityId

const mapStateToProps = createStructuredSelector({
  collateralBasketEntity: createWorkflowReferenceDataEntitySelector(EntityType.COLLATERAL_BASKET, workflowIdSelector, entityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector),
  getProductSector: createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  getProductSectorIdsForType: collateralBasketProductSectorIdsForTypeGetterSelector,
  getProduct: createEntityGetterSelector(EntityType.PRODUCT)
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const Content = styled(Groupbox)<{ width?: number }>`
  padding: 20px 20px 0 20px;
  min-height: 450px;
  overflow-y: auto;
  width: ${props => (props.width ? `${props.width}px` : '510px')};
`

export const getCustomIsinIssues = (collateralBasket: CollateralBasket | undefined): ValidationIssue[] => {
  if (!collateralBasket || (collateralBasket.collateralBasketIsin && collateralBasket.collateralBasketIsin?.length === 0)) return []

  return [
    collateralBasket.collateralBasketIsin && collateralBasket.collateralBasketIsin?.length < 12
      ? createInvalidValueValidationIssue('collateralBasketIsin', Locale.translate(Translation.SC_ISIN_LENGTH_ERROR), Severity.ERROR)
      : null
  ].filter(notNilFilter)
}

export const CollateralBasketDialogComponent: React.FunctionComponent<CollateralBasketDialogProps> = props => {
  const {
    workflowId,
    collateralBasketEntity,
    availableEvents,
    getProductSector,
    getProductSectorIdsForType,
    getProduct,
    onUpdate,
    onSubmitWorkflow,
    onCancelWorkflow,
    onClose
  } = props

  const [selectedTab, setSelectedTab] = useState(Translation.TC_OVERVIEW)
  const readOnly = useMemo(() => !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE), [availableEvents])
  const hasValidationIssues = useMemo(
    () => !!collateralBasketEntity?.validationIssues && collateralBasketEntity.validationIssues.length > 0,
    [collateralBasketEntity]
  )

  const onChangeTab = useCallback(
    (tab: Translation) => {
      setSelectedTab(tab)
    },
    [setSelectedTab]
  )

  const collateralBasket = collateralBasketEntity?.data

  const hasClientValidationissues = getCustomIsinIssues(collateralBasket).length > 0

  const onUpdateCollateralBasket = useCallback(
    (partialCollateralBasket: Partial<CollateralBasket>) => {
      if (collateralBasket) {
        onUpdate({
          entityId: collateralBasket.id,
          entityType: EntityTypeEnum.COLLATERAL_BASKET,
          updatedEntity: { ...collateralBasket, ...partialCollateralBasket },
          workflowId
        })
      }
    },
    [collateralBasket, workflowId, onUpdate]
  )

  const setDefaultProductSector = () => {
    onUpdateCollateralBasket({ productSectorId: getProductSectorIdsForType(CollateralBasketType.GC_BESPOKE)[0] })
    return null
  }

  const onSave = useCallback(() => {
    onSubmitWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }, [workflowId, onSubmitWorkflow, onClose])

  const handleClose = useCallback(() => {
    onCancelWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }, [onCancelWorkflow, onClose, workflowId])

  const isNewCollateralBasket = collateralBasketEntity?.entityState === EntityState.NEW

  const collateralBasketProductsFilteredValidations = collateralBasketEntity?.validationIssues?.filter(
    validationIssue => validationIssue.fieldName === 'collateralIds'
  )

  const collateralBasketProductSector = collateralBasket?.productSectorId
    ? getProductSector(collateralBasket.productSectorId)
    : setDefaultProductSector()

  const collateralBasketProduct = collateralBasketProductSector?.productId ? getProduct(collateralBasketProductSector.productId) : null
  const isGcBasket = collateralBasketProductSector?.basketType === CollateralBasketType.GC_BESPOKE

  const getSelectedTabContent = (tab: Translation) => {
    if (!collateralBasket) return null

    switch (tab) {
      case Translation.TC_OVERVIEW:
        return (
          <Content>
            <CollateralBasketOverviewPanel
              collateralBasket={collateralBasket}
              collateralBasketProductSector={collateralBasketProductSector}
              collateralBasketProduct={collateralBasketProduct}
              getProductSectorIdsForType={getProductSectorIdsForType}
              validationIssues={collateralBasketEntity.validationIssues}
              isNewCollateralBasket={isNewCollateralBasket}
              readOnly={readOnly}
              onUpdate={onUpdateCollateralBasket}
            />
          </Content>
        )
      case Translation.TC_PRODUCTS:
        return (
          <Content width={collateralBasket.collateralIds.length === 0 ? 540 : 1100}>
            <CollateralBasketProductsPanel
              collateralBasket={collateralBasket}
              collateralBasketProductSector={collateralBasketProductSector}
              readOnly={readOnly}
              validationIssues={collateralBasketProductsFilteredValidations ?? []}
              onUpdate={onUpdateCollateralBasket}
              viewOnly={false}
            />
          </Content>
        )
      case Translation.TC_PERMISSIONS:
        return (
          <Content width={collateralBasket.tradingEntityIds.length === 0 ? 540 : 1030}>
            <CollateralBasketPermissionsPanel
              collateralBasket={collateralBasket}
              collateralBasketProductSector={collateralBasketProductSector}
              onUpdate={onUpdateCollateralBasket}
              readOnly={readOnly}
              viewOnly={false}
            />
          </Content>
        )
      default:
        return <Content />
    }
  }

  if (!collateralBasketEntity) {
    return null
  }

  const title = isNewCollateralBasket ? Locale.translate(Translation.TC_ADD_NEW_COLLATERAL_BASKET) : collateralBasketEntity.data.name

  return (
    <ModalWindow title={title}>
      <Tabs
        dataTestId={'CollateralBasketDialog-Tabs'}
        selectedTab={selectedTab}
        onChangeTab={onChangeTab}
        tabs={[Translation.TC_OVERVIEW, Translation.TC_PRODUCTS, isGcBasket ? Translation.TC_PERMISSIONS : Translation.TC_EMPTY_HEADER]}
      />
      {getSelectedTabContent(selectedTab)}
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'CollateralBasketDialog-TextButton-ButtonCancel'} type={TextButtonType.Primary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'CollateralBasketDialog-TextButton-ButtonSave'}
              type={TextButtonType.Primary}
              disabled={readOnly || hasValidationIssues || hasClientValidationissues}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const CollateralBasketDialog = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(CollateralBasketDialogComponent)
